import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { useEventCallback, useStateWithDeps } from '@gitbook/util-react-hooks';

/**
 * A sync the user triggered from this session, before the backend reports it as running.
 */
export type InitiatedGitSync = {
    /** Client time the sync was triggered at, compared against the remote operation's `startedAt`. */
    at: number;
    direction: api.GitSyncOperationDirection;
};

/**
 * The Git Sync target currently in scope, and the syncs triggered on it from this session.
 */
export type GitSyncCurrentTarget = {
    target: api.Space | api.Site;
    initiatedSync: InitiatedGitSync | null;
    onSyncInitiated: (direction: api.GitSyncOperationDirection) => void;
};

const GitSyncTargetStatusContext = React.createContext<GitSyncCurrentTarget | null>(null);

/**
 * Track the syncs triggered from this session for a screen's Git Sync target, so every status
 * surface below reports `running` as soon as the user asks for a sync — the backend takes a moment
 * to start the operation, and until it does the realtime channel still replays the previous one.
 *
 * Mount it above everything that renders the target's status: the screen, its header, and the
 * installation dialog that triggers the sync.
 */
export function GitSyncTargetStatusProvider(props: {
    target: api.Space | api.Site;
    children: React.ReactNode;
}): React.ReactElement {
    const { target, children } = props;

    // Switching to another target drops any sync initiated on the previous one.
    const [initiatedSync, setInitiatedSync] = useStateWithDeps<InitiatedGitSync | null>(null, [
        target.id,
    ]);

    const onSyncInitiated = useEventCallback((direction: api.GitSyncOperationDirection) => {
        setInitiatedSync({ at: Date.now(), direction });
    });

    const value = React.useMemo<GitSyncCurrentTarget>(
        () => ({ target, initiatedSync, onSyncInitiated }),
        [target, initiatedSync, onSyncInitiated]
    );

    return (
        <GitSyncTargetStatusContext.Provider value={value}>
            {children}
        </GitSyncTargetStatusContext.Provider>
    );
}

/**
 * The Git Sync target in scope, along with any sync triggered on it from this session and the
 * callback to report a new one. `onSyncInitiated` makes every status surface for the target report
 * `running` right away, without waiting for the backend to pick the operation up.
 */
export function useGitSyncCurrentTarget(): GitSyncCurrentTarget {
    const context = React.useContext(GitSyncTargetStatusContext);

    if (!context) {
        throw new Error(
            'Git Sync target status is only available within a <GitSyncTargetStatusProvider>'
        );
    }

    return context;
}
